import { apiMarketoCreateLead, apiMarketoGetLead } from "../../../api/marketo";
import { getVueTemplate } from "../../_imports/utils/utils.common";
import BaseForm from "./base-form.vue";
import acceptedCookiePolicy from '../../../components/onetrust/cookiePolicy';

export default {
	name: 'marketo-form',
	template: getVueTemplate('marketo-form'),
	mixins: [
		BaseForm
	],
	props: {
		overrideThankYouMessage: String,
		overrideMarketoDetailIdUrl: String,
	},
	data() {
		return {
			verifyEmailFirst: false,
			alwaysAskEmail: false,
			fieldsAreVisible: true,
			fieldsAlreadyInMarketo: [],
			hideRequiredText : true,
			zoomInfo: {
				callback: null,
				fields: {},
				devMode: window.location.search.includes('zi_debug')
			}
		}
	},
	created() {
        if (this.data?.fillBehavior) {
            this.verifyEmailFirst = this.data.fillBehavior.includes('verify-email-first');
            this.alwaysAskEmail = this.data.fillBehavior.includes('always-ask-email');
            this.enrichedWithZoomInfo = this.data.fillBehavior.includes('enrich-with-zoominfo');
            this.fieldsAreVisible = !this.verifyEmailFirst;
            
            if (!this.alwaysAskEmail) {
                CoveoWebsite.forms.email = Cookies.get('marketo_id');
            }
    
            if (this.fieldsAreVisible) {
                this.hideRequiredText = false;
            }
        }
	},
	mounted() {
		if (this.enrichedWithZoomInfo
			&& !('_zi' in window)) {
			window._zi = {
				formId: 'lrlGyaAGnZHFIIgUKkUM',
				development: this.zoomInfo.devMode,
				callbacks: {
					onMatch: data => {
						if (data) {
							this.setZoomInfoFields(data);
						}
					}
				}
			};

			loadJS('https://ws-assets.zoominfo.com/formcomplete.js');
		}	
	},
	methods: {
		getMarketoId() {
			return Cookies.get('marketo_id');
		},
		isValidMarketoId() {
			const marketoId = this.getMarketoId();

			return marketoId
				&& marketoId !== 'ot';
		},
		addClassOnBody() {
			if(this.getMarketoId()) {
				document.body.classList.add('is-existing-marketo-lead');

				return;
			}

			document.body.classList.add('is-new-marketo-lead');
		},
		async onSubmitForm(form) {
			const fields = {};
            
			const fieldsIgnoredByMarketo = this.registeredFields?.filter((field) => field.ignoredByMarketo).map((field) => field.name) ?? [];

			for (const field in form.fields) {
				if (fieldsIgnoredByMarketo.includes(field)) {
					continue;
				}

				const fieldValue = String(form.fields[field]);

				if (fieldValue) {
					fields[field] = fieldValue;
				}
			}

			if (fields.Email) {
				CoveoWebsite.forms.email = fields.Email;
				CoveoWebsite.forms.firstname = fields.FirstName || '';

				if (this.alwaysAskEmail
					&& this.isValidMarketoId()) {	
					this.$emit('form-submit');
					this.$root.$emit('form-submit');
	
					fields['mktoID'] = this.getMarketoId();
				}

				const isEnriched = this.registeredFields.some(field => field.isEnriched);
				
				if (isEnriched) {
					fields['mktoZoomInfoEnriched'] = 'true';
				}

				let marketoDetailIDUrl = this.overrideMarketoDetailIdUrl;
				
				if (!marketoDetailIDUrl) {
					marketoDetailIDUrl = fields['MarketoDetailIDUrl'];
				}

				if (!marketoDetailIDUrl) {
					marketoDetailIDUrl = window.location.pathname;
				}

				fields['MarketoDetailIDUrl'] = marketoDetailIDUrl;
				
				const hasListeners = this.$listeners['before-create-lead'];
				if (hasListeners) {
					await new Promise(resolve => {
						this.$emit('before-create-lead', { fields }, resolve);
					});
				}

				this.addClassOnBody();

				apiMarketoCreateLead({
					data: fields,
					success: () => {
						this.hideTheFields(form.success);
					},
					error: () => {
						form.error();
					}
				});
			}
		},
		onValidEmail(email) {
			if (this.alwaysAskEmail
				&& this.isValidMarketoId()) {
				this.fieldsAreVisible = false;

				const fieldsToDisconnect = this.registeredFieldsToFill.filter(field => !this.emailFields.includes(field)).map(field => field.name);

				this.sendEventWithName('disconnectfields', {
					fields: fieldsToDisconnect
				});

				this.updateState(state => state.formReadyForSubmit = true);
			} else if (this.verifyEmailFirst
				&& !this.state.formVerifyingEmail) {
				this.fieldsAreVisible = false;
				this.fieldsAlreadyInMarketo = [];
				this.sendEventWithName('connectfields');

				this.updateState(state => state.formVerifyingEmail = true);

				const fields = [];

				this.registeredFieldsToFill.forEach(field => {
					if (field.name
						&& !fields.includes(field.name)) {
						fields.push(field.name);
					}
				});

				apiMarketoGetLead({
					noLeadCreation: true,
					email,
					fields: fields.join(','),
					success: result => {
						if (result.fields) {
							const fieldsInMarketo = result.fields;

							this.registeredFieldsToFill.forEach(fieldForUser => {
								if (!fieldForUser.forceSendToMarketo
                                    && fieldsInMarketo[fieldForUser.name]) {
									this.fieldsAlreadyInMarketo.push(fieldForUser);
								}
							});

							this.sendEventWithName('disconnectfields', {
								fields: this.fieldsAlreadyInMarketo.map(field => field.name)
							});
						}

						if (result) {
							this.createMarketoCookieWithId(result.id);
						}

						this.updateState(state => {
							state.formVerifyingEmail = false;
							state.formEmailIsKnown = true;
						});

						this.$root.$emit('get-lead-success');
					},
					noResult: () => {
						this.updateState(state => {
							state.formVerifyingEmail = false;
							state.formEmailIsKnown = false;
							state.formSubmitSuccess = false;
						});
					},
					afterResponse: () => {
						if (this.enrichedWithZoomInfo) {
							this.sendEventWithName('unenrichfields');

							this.enrichWithZoomInfo(email)
								.then(fields => {
									fields?.forEach(field => {
										this.sendEventWithName('enrichfield', {
											name: field.name,
											value: field.value
										});
			
										this.sendEventWithName('hidefield', {
											name: field.name
										});
									});
									this.fieldsAreVisible = true;
								})
								.catch(() => this.fieldsAreVisible = true);
						} else {
							this.fieldsAreVisible = true;
						}
					}
				});
			}
		},
		hideTheFields(callback) {
			this.updateFormContentClasses('no-overflow', true);
			this.$emit('hide-arrow');
			Velocity(this.$refs.form, {
				height: 0,
			}, {
				duration: 500
			}).then(callback);
		},
		createMarketoCookieWithId(marketoId) {
			const domain = 'coveo.com';

			acceptedCookiePolicy({
				ifTargetingCookies: () => {
					Cookies.set('marketo_id', marketoId,  { domain: domain, expires: 7 });
				},
				ifNoTargetingCookies: () => {
					Cookies.set('marketo_id', 'ot',  { domain: domain });
				}
			});
		},
		enrichWithZoomInfo(email) {
			return new Promise((resolve, reject) => {
				const ziForm = document.querySelector('form[data-zi-form-enriched]');

				if (!ziForm) {
					return resolve(fields);
				}

				const ziEmailField = ziForm.querySelector('input[type="email"]');
				if (!ziEmailField) {
					return resolve(fields);
				}

				let resolved = false;

				this.whenZoomInfoFieldsAreReady(fields => {
					const formattedFields = Object.entries(fields).map(field => ({ name: field[0], value: field[1] }));
					resolved = true;

					resolve(formattedFields);
				});

				setTimeout(() => {
					if (!resolved) {
						reject();
					}
				}, 2000);

				ziEmailField.value = email;
				ziEmailField.dispatchEvent(new Event('input'));
			})
		},
		setZoomInfoFields(fields) {
			if (!fields) {
				return;
			}

			this.zoomInfo.fields = {};
			
			const prefix = 'zi_';

			for (let [key, value] of Object.entries(fields)) {
				if (key.startsWith(prefix)) {
					key = key.substring(prefix.length);
				}

				if (key.toLowerCase() === 'email'
					|| typeof value === 'undefined') {
					continue;
				}

				this.zoomInfo.fields[key] = value;
			}

			if (typeof this.zoomInfo.callback === 'function') {
				this.zoomInfo.callback(this.zoomInfo.fields);
				this.zoomInfo.callback = null;
			}
		},
		whenZoomInfoFieldsAreReady(callback) {
			this.zoomInfo.callback = callback;
		}
	},
	watch: {
		fieldsAreVisible() {
			this.$nextTick(() => {
				if (this.$refs.formFields) {
					const baseDuration = 1000;
					const normalHeightToBaseDuration = 500;
					const fieldsBaseHeight = 32;
					const fieldsHeight = this.$refs.formFields.scrollHeight;
					const duration = fieldsHeight / normalHeightToBaseDuration * baseDuration;

					if (this.fieldsAreVisible
						&& fieldsHeight > fieldsBaseHeight) {
						let opacityAnimStarted = false;

						Velocity(this.$refs.formFields, {
							height: [fieldsHeight + 12, 0],
							opacity: 0
						}, {
							duration: duration,
							easing: [900, 35],
							complete: () => {
								this.$refs.formFields.style.height = 'auto';
							},
							progress: (elements, complete) => {
								if (complete >= 0.1 && !opacityAnimStarted) {
									opacityAnimStarted = true;
	
									Velocity(this.$refs.formFields, {
										opacity: 1
									}, {
										delay: duration * 0.2,
										duration: 200
									});
								}
							},
							begin: () => {
								this.$root.$emit('show-form-fields-animation-begin');
							}
						});

						this.$emit('hide-arrow');
						
					} else {
						Velocity(this.$refs.formFields, {
							height: 0,
							opacity: 0
						}, {
							duration: duration,
							easing: [900, 35]
						});

						this.hideRequiredText = true;
					}
				}
			});

			if (this.fieldsAreVisible) {			
				this.hideRequiredText = false;
			}
		},
		state() {
			const isLargeWidth = this.state.formSubmitSuccess && this.data.messages.success.componentName === 'form-resource-document-success-message';
			this.updateFormRootClasses('large', isLargeWidth);
		}
	}
};